/**
 * Role Controller
 * 
 * Simple role-based permission system.
 * Role + Feature => Accessible or not
 * 
 * @module roleController
 */

import { useMemo } from 'react';
import useAuth from '../hooks/useAuth';
import { ROLES } from './roleConstants';
import { FEATURES } from './featurePermissions';
import { ROLE_FEATURES } from './roleMatrix';
import { USER_RESOURCES_LIST } from './constant';

/**
 * Hook to access role-based permissions
 * 
 * @returns {Object} Role controller with can() and getRole() methods
 * 
 * @example
 * ```javascript
 * const roleController = useRoleController();
 * const canUseDatePicker = roleController.can(FEATURES.DATE_PICKER);
 * const userRole = roleController.getRole();
 * ```
 */
export const useRoleController = () => {
  const { auth } = useAuth();
  
  const roleController = useMemo(() => {
    /**
     * Get user's role from auth
     * Hierarchy: BASIC < PREMIUM < PRO1 < PRO2 < ADMIN
     * @returns {string} Role constant (ROLES.BASIC, ROLES.PREMIUM, ROLES.PRO1, or ROLES.PRO2)
     */
    const getUserRole = () => {
      // Check PRO2 first (highest tier before admin)
      if (auth?.role?.includes(ROLES.PRO2)) {
        return ROLES.PRO2;
      }
      if (auth?.role?.includes(ROLES.PRO1)) {
        return ROLES.PRO1;
      }
      if (auth?.role?.includes(ROLES.PREMIUM)) {
        return ROLES.PREMIUM;
      }
      return ROLES.BASIC;
    };
    
    const userRole = getUserRole();
    
    /**
     * Build feature list once per refresh (when auth changes)
     * - BASIC users: BASIC features only
     * - PREMIUM users: BASIC + PREMIUM features
     * - PRO1 users: BASIC + PREMIUM + PRO1 features
     * - PRO2 users: BASIC + PREMIUM + PRO1 + PRO2 features
     */
    const userFeatures = (() => {
      const features = [];
      
      // Always include BASIC (everyone gets basic)
      features.push(...ROLE_FEATURES[ROLES.BASIC]);
      
      // PREMIUM users get PREMIUM features (PRO1 and PRO2 also get PREMIUM features)
      if (userRole === ROLES.PREMIUM || userRole === ROLES.PRO1 || userRole === ROLES.PRO2) {
        features.push(...ROLE_FEATURES[ROLES.PREMIUM]);
      }
      
      // PRO1 users get PRO1 features (PRO2 also gets PRO1 features)
      if (userRole === ROLES.PRO1 || userRole === ROLES.PRO2) {
        features.push(...ROLE_FEATURES[ROLES.PRO1]);
      }
      
      // PRO2 users get PRO2 features 
      if (userRole === ROLES.PRO2) {
        features.push(...ROLE_FEATURES[ROLES.PRO2]);
      }
      
      return features;
    })();
    
    return {
      /**
       * Check if user can access a feature
       * Role + Feature => Accessible or not
       * 
       * @param {string} feature - Feature constant from FEATURES
       * @returns {boolean} True if user has access
       */
      can: (feature) => {
        // Use pre-built userFeatures (no rebuilding on each call)
        if (!userFeatures.includes(feature)) {
          return false;
        }
        
        // Check resources if feature requires them
        // Only MP_CHART_STOCKS requires resource check (stocks add-on)
        if (feature === FEATURES.MP_CHART_STOCKS) {
          return auth?.resources?.includes(USER_RESOURCES_LIST.MP_STOCKS) || false;
        }
        
        // Orderflow features (OF_CHART_INDEX, OF_CHART_STOCKS, BALANCE_IMBALANCE, 
        // LINE_CHART_ELITE, GLOBAL_BOOKMARKS) - no resource enforcement from frontend
        // Access is based on role only (PRO1 = Elite tier)
        
        return true;
      },
      
      /**
       * Get user's current role
       * @returns {string} Role constant (ROLES.BASIC, ROLES.PREMIUM, ROLES.PRO1, or ROLES.PRO2)
       */
      getRole: () => userRole
    };
  }, [auth?.role, auth?.resources]);
  
  return roleController;
};

export default useRoleController;

